import { Link } from 'react-router-dom'
import { Network, X } from 'lucide-react'
import type { ShiftDashboardFilterOptions } from '@/api/shiftDashboardApi'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { groupsForTeam, roomsForSite, type ShiftFilters } from '@/lib/shiftDashboard'
import { cn } from '@/lib/utils'

// Radix Select cannot hold an empty value, so "All" is a sentinel.
const ALL = '__all'

/**
 * The Shift Dashboard's global filters (z8nrz7fbcm). When, then where, then
 * who: day and shift first because they define the window everything else is
 * counted in. Room follows site and Group follows Team — each list only offers
 * what is inside the choice before it. Teams are defined in Resources → Teams;
 * this bar only links there.
 */
export function ShiftFilterBar({
  filters,
  options,
  onChange,
  onClear,
  canManageTeams,
}: {
  filters: ShiftFilters
  options: ShiftDashboardFilterOptions | undefined
  onChange: (key: keyof ShiftFilters, value: string | null) => void
  /** Clears site, room, shift, team and group; keeps the day. */
  onClear: () => void
  /** Whether to show the way to Resources → Teams. */
  canManageTeams: boolean
}) {
  const sites = options?.sites ?? []
  const rooms = options ? roomsForSite(options.rooms, filters.siteId) : []
  const shifts = options?.shifts ?? []
  const teams = options?.teams ?? []
  const groups = options ? groupsForTeam(options, filters.teamId) : []
  const anySet = !!(filters.siteId || filters.roomId || filters.shiftDefinitionId || filters.teamId || filters.groupId)

  return (
    <div className='rounded-xl border border-gray-200 bg-white shadow-sm p-3'>
      <div className='flex flex-wrap items-end gap-x-3 gap-y-3'>
        <Field label='Date' htmlFor='shift-dashboard-date'>
          <Input
            id='shift-dashboard-date'
            type='date'
            value={filters.date}
            onChange={(e) => e.target.value && onChange('date', e.target.value)}
            className='h-9 w-40'
          />
        </Field>
        <FilterSelect
          label='Shift'
          value={filters.shiftDefinitionId}
          onChange={(v) => onChange('shiftDefinitionId', v)}
          items={shifts.map((s) => ({
            id: s.id,
            label: `${s.name} (${s.startTime}–${s.endTime})${s.active ? '' : ', inactive'}`,
          }))}
          allLabel='Whole day'
          disabled={!options}
          emptyHint='No shifts set up'
        />

        <span aria-hidden className='hidden h-9 w-px bg-border lg:block' />

        <FilterSelect
          label='Site'
          value={filters.siteId}
          onChange={(v) => onChange('siteId', v)}
          items={sites.map((s) => ({ id: s.id, label: s.name }))}
          allLabel='All sites'
          disabled={!options}
          emptyHint='No sites set up'
        />
        <FilterSelect
          label='Room'
          value={filters.roomId}
          onChange={(v) => onChange('roomId', v)}
          items={rooms.map((r) => ({ id: r.id, label: r.label }))}
          allLabel={filters.siteId ? 'All rooms on this site' : 'All rooms'}
          disabled={!options}
          emptyHint='No rooms'
        />

        <span aria-hidden className='hidden h-9 w-px bg-border lg:block' />

        <FilterSelect
          label='Team'
          value={filters.teamId}
          onChange={(v) => onChange('teamId', v)}
          items={teams.map((t) => ({ id: t.id, label: t.name }))}
          allLabel='All teams'
          disabled={!options}
          emptyHint='No teams set up'
        />
        <FilterSelect
          label='Group'
          value={filters.groupId}
          onChange={(v) => onChange('groupId', v)}
          items={groups.map((g) => ({ id: g.id, label: g.name }))}
          allLabel={filters.teamId ? 'All groups in this team' : 'All groups'}
          disabled={!options}
          emptyHint='No groups'
        />

        <div className='ml-auto flex items-center gap-1'>
          {anySet && (
            <button
              type='button'
              onClick={onClear}
              className='inline-flex h-9 items-center gap-1 rounded-md px-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground'
            >
              <X className='h-3.5 w-3.5' /> Clear filters
            </button>
          )}
          {canManageTeams && (
            <Link
              to='/resources?tab=teams'
              title='Teams and their groups are set up in Resources → Teams'
              className='inline-flex h-9 items-center gap-1.5 rounded-md px-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground'
            >
              <Network className='h-4 w-4' /> Manage teams
            </Link>
          )}
        </div>
      </div>
    </div>
  )
}

function Field({ label, htmlFor, children }: { label: string; htmlFor: string; children: React.ReactNode }) {
  return (
    <div className='grid gap-1'>
      <Label htmlFor={htmlFor} className='text-xs font-medium text-muted-foreground'>
        {label}
      </Label>
      {children}
    </div>
  )
}

function FilterSelect({
  label,
  value,
  onChange,
  items,
  allLabel,
  disabled,
  emptyHint,
}: {
  label: string
  value: string | null
  onChange: (value: string | null) => void
  items: { id: string; label: string }[]
  allLabel: string
  disabled?: boolean
  /** Said in the list when there is nothing to choose. */
  emptyHint: string
}) {
  const id = `shift-dashboard-${label.toLowerCase()}`
  return (
    <Field label={label} htmlFor={id}>
      <Select value={value ?? ALL} onValueChange={(v) => onChange(v === ALL ? null : v)} disabled={disabled}>
        <SelectTrigger id={id} className={cn('h-9 w-44', value && 'border-foreground/40 font-medium')}>
          <SelectValue placeholder={allLabel} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{allLabel}</SelectItem>
          {items.map((item) => (
            <SelectItem key={item.id} value={item.id}>
              {item.label}
            </SelectItem>
          ))}
          {items.length === 0 && <p className='px-2 py-1.5 text-xs text-muted-foreground'>{emptyHint}</p>}
        </SelectContent>
      </Select>
    </Field>
  )
}
